import React, { useState, useRef } from 'react';
import { useProjectAccess } from '../hooks/useProjectAccess';
import { useClickOutside } from '../hooks/useClickOutside';
import { SettingsIcon } from './icons/SettingsIcon';
import SettingsSidebar from './settings/SettingsSidebar';
import SettingsMenu from './settings/SettingsMenu';
import BackgroundPicker from './BackgroundPicker';
import { ProjectMembers } from './ProjectMembers';
import ArchivedItems from './ArchivedItems';
import BoardActivity from './board/BoardActivity';
import { useProjectData } from '../hooks/useProjectData';
import { SettingsButton } from '../styles/ProjectSettingsMenu.styles';

const ProjectSettingsMenu = ({
    currentBackground,
    onBackgroundChange,
    projectId,
    projectTitle,
    archivedCards = [],
    onCardClick,
    limitColumnHeight,
    onToggleLimitColumnHeight,
    enableGithubLinks, // Add new prop
    onToggleEnableGithubLinks, // Add new handler prop
    onExportProject // Export handler prop
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeSection, setActiveSection] = useState(null);
    const { project, projectMembers } = useProjectData(projectId);
    const sidebarRef = useRef(null);
    const { isAdmin, role } = useProjectAccess(projectId);

    useClickOutside(sidebarRef, handleClose, isOpen);

    function handleClose() {
        setIsOpen(false);
        setActiveSection(null);
    }

    const handleBackgroundChange = (bg) => {
        onBackgroundChange(bg);
        handleClose();
    };

    const renderActiveSection = () => {
        switch (activeSection) {
            case 'background':
                return (
                    <BackgroundPicker
                        currentBackground={currentBackground}
                        projectTitle={projectTitle}
                        onSelectBackground={handleBackgroundChange}
                        onClose={handleClose}
                    />
                );
            case 'members':
                return (
                    <ProjectMembers
                        projectId={projectId}
                        currentUserRole={role}
                    />
                );
            case 'archived':
                return (
                    <ArchivedItems
                        archivedCards={archivedCards}
                        onCardClick={(card) => {
                            onCardClick(card);
                            handleClose();
                        }}
                    />
                );
            case 'activity':
                const allActivities = project?.activities || [];
                const cardActivities = Object.values(project?.columns || {}).flatMap(col => col.cards ? col.cards.flatMap(card => (card.activity || []).map(a => ({...a, cardId: card.id, cardName: card.name}))) : []);

                return (
                    <BoardActivity
                        boardActivities={allActivities}
                        cardActivities={cardActivities}
                        projectMembers={projectMembers}
                    />
                );
            default:
                return (
                    <SettingsMenu
                        isAdmin={isAdmin}
                        archivedCards={archivedCards}
                        onSectionChange={setActiveSection}
                        limitColumnHeight={limitColumnHeight}
                        onToggleLimitColumnHeight={onToggleLimitColumnHeight}
                        enableGithubLinks={enableGithubLinks} // Pass down
                        onToggleEnableGithubLinks={onToggleEnableGithubLinks} // Pass down
                        onExportProject={() => { if (onExportProject) { onExportProject(); handleClose(); } }}
                    />
                );
        }
    };

    return (
        <>
            <SettingsButton
                onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(!isOpen);
                }}
                title="Board settings"
            >
                <SettingsIcon />
            </SettingsButton>

            {isOpen && (
                <SettingsSidebar
                    activeSection={activeSection}
                    onClose={handleClose}
                    forwardedRef={sidebarRef}
                >
                    {renderActiveSection()}
                </SettingsSidebar>
            )}
        </>
    );
};

export default ProjectSettingsMenu;